import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { CardWatchLogo, CardWatchMark } from "@/components/CardWatchLogo";
import ManualCardDialog from "@/components/ManualCardDialog";
import LinkCardChooserDialog from "@/components/LinkCardChooserDialog";
import UpgradeDialog from "@/components/UpgradeDialog";
import SettingsDialog from "@/components/SettingsDialog";
import CardVault from "@/components/CardVault";
import TransactionHistory from "@/components/TransactionHistory";
import CategoryBreakdown from "@/components/CategoryBreakdown";
import DueSoonBanner from "@/components/DueSoonBanner";
import BudgetAlertBanner from "@/components/BudgetAlertBanner";
import StreakRibbon from "@/components/StreakRibbon";
import OnboardingTour, { useShouldShowTour } from "@/components/OnboardingTour";
import BottomNav from "@/components/BottomNav";
import ProLock from "@/components/ProLock";
import ProExpiredBanner from "@/components/ProExpiredBanner";
import SaltEdgeOverrideDialog from "@/components/SaltEdgeOverrideDialog";
import GoCardlessConnectDialog from "@/components/GoCardlessConnectDialog";
import {
  Plus,
  ArrowRight,
  CreditCard,
  RefreshCw,
  Wallet,
  TrendingDown,
  Gauge,
  Sparkles,
  Trash2,
  Pencil,
  CalendarDays,
  Settings as SettingsIcon,
  SlidersHorizontal,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencyFormat = (value, currency = "GBP") => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const shortDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
};

const wasRecentlyRolled = (rolledAtIso, windowDays = 14) => {
  if (!rolledAtIso) return false;
  const t = Date.parse(rolledAtIso);
  if (Number.isNaN(t)) return false;
  const days = (Date.now() - t) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= windowDays;
};

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short" });
};

const utilizationColor = (util) => {
  if (util === null || util === undefined) return "#64748B";
  if (util < 30) return "#00E676";
  if (util < 60) return "#F5D742";
  return "#FF6B6B";
};

const StatCard = ({ label, value, sub, icon: Icon, accent = false, testid }) => (
  <div
    data-testid={testid}
    className="relative bg-[#0E172C] border border-[#1A2849] rounded-2xl p-6 overflow-hidden"
  >
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
        {label}
      </span>
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          accent ? "bg-[#00E676]/15 border border-[#00E676]/30" : "bg-white/[0.03] border border-white/5"
        }`}
      >
        <Icon size={16} className={accent ? "text-[#00E676]" : "text-slate-300"} />
      </div>
    </div>
    <div
      className="mt-6 text-3xl font-bold tracking-tight text-white"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-300">{sub}</div>}
    {accent && (
      <div className="pointer-events-none absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-[#00E676]/10 blur-2xl" />
    )}
  </div>
);

const UtilizationBar = ({ util }) => {
  const clamped = util === null || util === undefined ? 0 : Math.min(100, Math.max(0, util));
  const color = utilizationColor(util);
  return (
    <div className="w-full h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${clamped}%`, backgroundColor: color, boxShadow: `0 0 10px ${color}66` }}
      />
    </div>
  );
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
};

const EmptyState = ({ onConnect, onManual, loading }) => (
  <div
    className="bg-[#0E172C] border border-dashed border-[#1A2849] rounded-2xl p-10 text-center"
    data-testid="linked-cards-empty-state"
  >
    <div className="mx-auto w-14 h-14 rounded-2xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
      <CreditCard size={22} className="text-[#00E676]" />
    </div>
    <h3
      className="mt-5 text-white text-xl font-semibold tracking-tight"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      No credit cards linked yet
    </h3>
    <p className="mt-2 text-sm text-slate-300 max-w-md mx-auto">
      Add a card manually to start tracking balances, due dates and utilization. Automatic bank sync via Plaid keeps your balances live.
    </p>
    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
      <button
        onClick={onConnect}
        disabled={loading}
        data-testid="empty-state-connect-button"
        className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-5 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60"
      >
        Add a credit card
        <Plus size={14} />
      </button>
      <button
        onClick={onManual}
        data-testid="empty-state-manual-button"
        className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-[#00E676]/40 px-5 py-2.5 text-xs text-slate-200 hover:text-white transition-colors"
      >
        Manual card entry
      </button>
    </div>
  </div>
);

import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, User as UserIcon, FileText } from "lucide-react";

const DEMO_USER_ID = "cardwatch-demo-user";

const Dashboard = ({ demoMode = false }) => {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeUserId = demoMode ? DEMO_USER_ID : user?.user_id;
  const readOnly = demoMode; // /demo hides write actions
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [syncingCards, setSyncingCards] = useState(false);
  const [gcOpen, setGcOpen] = useState(false);
  const [gcEnabled, setGcEnabled] = useState(false);
  const [error, setError] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [linkChooserOpen, setLinkChooserOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editingSaltEdge, setEditingSaltEdge] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [reminderWindow, setReminderWindow] = useState(2);
  const [remindersDismissed, setRemindersDismissed] = useState(false);
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [budgetsDismissed, setBudgetsDismissed] = useState(false);
  const [activityTick, setActivityTick] = useState(0);
  const requestSeq = useRef(0);

  const loadDashboard = async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const dashRes = await axios.get(`${API}/saltedge/dashboard`, {
        params: { user_id: activeUserId },
        withCredentials: true,
      });
      if (seq !== requestSeq.current) return;
      setData(dashRes.data);
      setActivityTick((t) => t + 1);
      setLoading(false);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : e.message || "Failed to load dashboard";
      setError(msg);
      setLoading(false);
    }
    // Reminders are a nice-to-have — fetched separately so a stall on that endpoint
    // never blocks the dashboard from rendering.
    try {
      const remRes = await axios.get(`${API}/reminders/due_soon`, {
        params: { user_id: activeUserId, within_days: 2 },
        withCredentials: true,
      });
      if (seq !== requestSeq.current) return;
      setReminders(remRes.data?.data || []);
      setReminderWindow(remRes.data?.within_days ?? 2);
    } catch (_) {
      // silent — banner will simply not appear if reminders fail
    }

    // Budget/cap status — same non-blocking pattern.
    try {
      const budRes = await axios.get(`${API}/budgets/status`, {
        params: { user_id: activeUserId },
        withCredentials: true,
      });
      if (seq !== requestSeq.current) return;
      setBudgetAlerts(budRes.data?.banner || []);
    } catch (_) {
      // silent — banner will just not show
    }
  };

  useEffect(() => {
    if (activeUserId) loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId]);

  // GoCardless feature flag — hides the button when creds are missing / disabled
  useEffect(() => {
    if (demoMode) return;
    axios
      .get(`${API}/gocardless/status`, { withCredentials: true })
      .then((res) => setGcEnabled(!!res.data?.enabled))
      .catch(() => setGcEnabled(false));
  }, [demoMode]);

  // GoCardless return handler — GoCardless appends ?ref=<reference> to our redirect
  useEffect(() => {
    if (demoMode) return;
    const params = new URLSearchParams(location.search);
    if (params.get("linked") !== "gocardless") return;
    const ref = params.get("ref");
    const error = params.get("error");
    if (error) {
      toast.error(`Bank link cancelled: ${error}`);
      navigate("/dashboard", { replace: true });
      return;
    }
    if (!ref) return;
    let cancelled = false;
    (async () => {
      try {
        // The reference we sent to GoCardless is our own — but the requisition_id
        // is what we need on the backend. Look it up by reference on the server.
        const lookup = await axios.get(`${API}/gocardless/accounts`, { withCredentials: true });
        // If accounts exist already, we're done — celebrate and refresh.
        if (Array.isArray(lookup.data?.accounts) && lookup.data.accounts.length > 0) {
          if (!cancelled) {
            toast.success("Bank connected — new cards will appear below");
            await loadDashboard();
            navigate("/dashboard", { replace: true });
          }
          return;
        }
        // Otherwise ask the backend to finalize by requisition ref.
        await axios.post(
          `${API}/gocardless/finalize`,
          { reference: ref },
          { withCredentials: true }
        );
        if (!cancelled) {
          toast.success("Bank connected via GoCardless");
          await loadDashboard();
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Could not complete GoCardless link");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, demoMode]);

  // Plaid callback handling happens inside PlaidLinkButton (public_token →
  // /api/plaid/exchange) — no URL-return flow like the old TrueLayer OAuth.

  // Bottom nav → Settings tab passes state.openSettings=true
  useEffect(() => {
    if (location.state?.openSettings) {
      setSettingsOpen(true);
      // Clear state so back/refresh doesn't reopen it
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Stripe checkout return handler — poll status, flip Pro locally, celebrate
  useEffect(() => {
    if (demoMode) return;
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout === "cancel") {
      toast.info("Checkout cancelled — you can upgrade any time");
      navigate("/dashboard", { replace: true });
      return;
    }
    if (checkout !== "success" || !sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 15; // ~30s
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          withCredentials: true,
        });
        if (res.data?.payment_status === "paid") {
          toast.success("Welcome to CardWatch Pro — unlimited unlocked");
          if (refresh) await refresh();
          await loadDashboard();
          navigate("/dashboard", { replace: true });
          return;
        }
      } catch (e) {
        // ignore transient errors and keep polling
      }
      if (attempts >= maxAttempts) {
        toast.info("Payment received — refresh in a moment if Pro isn't active yet");
        navigate("/dashboard", { replace: true });
        return;
      }
      window.setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, demoMode]);

  const handleSyncCards = async () => {
    setSyncingCards(true);
    try {
      const res = await axios.post(
        `${API}/plaid/sync`,
        {},
        { withCredentials: true }
      );
      const cards = res.data?.cards || 0;
      const items = res.data?.items || 0;
      if (items === 0) {
        toast.info("No linked banks yet — tap 'Add a credit card' to get started");
      } else {
        toast.success(`Synced ${cards} card${cards === 1 ? "" : "s"} across ${items} bank${items === 1 ? "" : "s"}`);
      }
      await loadDashboard();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed");
    } finally {
      setSyncingCards(false);
    }
  };

  const handleDeleteCard = async (cardId, name) => {
    try {
      await axios.delete(`${API}/cards/${cardId}`, { withCredentials: true });
      // Optimistic reload; then show an Undo toast for 5s.
      loadDashboard();
      toast.success(`Removed ${name}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(`${API}/cards/${cardId}/restore`, {}, { withCredentials: true });
              toast.success(`Restored ${name}`);
              loadDashboard();
            } catch (e) {
              toast.error("Could not restore card");
            }
          },
        },
      });
    } catch (e) {
      toast.error("Could not remove card");
    }
  };

  const handleRemoveVaultCard = async (card) => {
    if (!card?.id) return;
    const label = card.nickname || card.name;
    if (card.source === "manual") {
      await handleDeleteCard(card.id, label);
      return;
    }
    // Plaid-linked card — soft-hide via override endpoint, with Undo
    try {
      await axios.delete(`${API}/saltedge/accounts/${card.id}`, { withCredentials: true });
      loadDashboard();
      toast.success(`Removed ${label}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(
                `${API}/saltedge/accounts/${card.id}/restore`,
                {},
                { withCredentials: true }
              );
              toast.success(`Restored ${label}`);
              loadDashboard();
            } catch (e) {
              toast.error("Could not restore card");
            }
          },
        },
      });
    } catch (e) {
      toast.error("Could not remove card");
    }
  };

  const spendingData = useMemo(() => {
    const series = data?.spending_series || [];
    return series.map((p) => ({ ...p, label: monthLabel(p.month) }));
  }, [data]);

  const currency = data?.currency || "GBP";
  const homeCurrency = data?.settings?.home_currency || "GBP";
  const fxRates = data?.fx?.rates || null;
  const plan = data?.plan || { name: "free", card_limit: 2, at_limit: false, counts: { total: 0 } };
  const isPro = plan?.name === "pro";
  const util = data?.utilization;
  const monthSpend = spendingData.length ? spendingData[spendingData.length - 1].amount : 0;
  const linkedCards = data?.linked_card_count || 0;
  const manualCards = data?.manual_cards || [];
  const seAccounts = data?.accounts || [];
  const perCard = util?.per_card || [];

  // Guard actions with the plan limit. Returns true if the caller can proceed.
  const enforcePlanLimit = () => {
    if (plan?.at_limit) {
      setUpgradeOpen(true);
      return false;
    }
    return true;
  };

  const openManualCreate = () => {
    if (!enforcePlanLimit()) return;
    setEditingCard(null);
    setManualOpen(true);
  };

  const openManualEdit = (card) => {
    // Editing an existing card never counts against the limit
    setEditingCard({
      id: card.id,
      name: card.name,
      balance: card.balance,
      credit_limit: card.credit_limit,
      due_date: card.due_date,
      currency: card.currency,
      nickname: card.nickname,
      last_four: card.last_four,
    });
    setManualOpen(true);
  };

  // Deep-link support: `/dashboard?add=1` from Landing's hero CTA auto-opens
  // the "Add a credit card" chooser once the user + plan are loaded.
  useEffect(() => {
    if (readOnly || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get("add") === "1") {
      setLinkChooserOpen(true);
      // strip the flag so refresh/back doesn't re-trigger the modal
      params.delete("add");
      const rest = params.toString();
      navigate(rest ? `/dashboard?${rest}` : "/dashboard", { replace: true });
    }
  }, [location.search, readOnly, user, navigate]);

  const guardedConnect = async () => {
    if (!enforcePlanLimit()) return;
    // Show the chooser dialog first — signed-in users pick between the
    // (temporarily disabled) automatic bank sync and manual entry. The
    // guardedConnect entry point is only wired to signed-in UI; unauthed
    // landing-page CTAs continue to redirect to /signup upstream.
    setLinkChooserOpen(true);
  };

  // Onboarding tour — first-time signed-in users only, never in /demo
  const { shouldShow: showTour } = useShouldShowTour(user, demoMode);

  return (
    <div className="min-h-screen bg-[#060B19] text-white">
      {showTour && !loading && (
        <OnboardingTour
          user={user}
          onComplete={() => { if (refresh) refresh(); }}
        />
      )}
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/30 blur-[160px]" />

      <header className="relative z-10 bg-[#060B19]/70 backdrop-blur-xl border-b border-white/5">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <Link to="/" data-testid="dashboard-logo-link" className="flex-shrink-0">
              <span className="sm:hidden inline-block">
                <CardWatchMark size={34} />
              </span>
              <span className="hidden sm:inline-block">
                <CardWatchLogo size={44} />
              </span>
            </Link>
            {plan?.name === "pro" && (
              <span
                data-testid="header-pro-badge"
                className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/50 bg-[#00E676]/[0.08] px-1.5 py-0.5 sm:px-2.5 sm:py-1 text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#00E676] font-bold shadow-[0_0_16px_-4px_rgba(0,230,118,0.5)]"
              >
                <Sparkles size={9} className="sm:hidden" />
                <Sparkles size={10} className="hidden sm:inline" />
                Pro
              </span>
            )}
          </div>
          <div className="hidden lg:flex items-center gap-2 text-xs text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] shadow-[0_0_8px_#00E676] animate-pulse" />
            Live · powered by Plaid
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {readOnly ? (
              <>
                <span
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold"
                  data-testid="demo-mode-badge"
                >
                  Demo mode
                </span>
                <Link
                  to="/signup"
                  data-testid="demo-signup-cta"
                  className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-3 sm:px-5 py-2 sm:py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
                >
                  <span className="hidden sm:inline">Sign up to add your own</span>
                  <span className="sm:hidden">Sign up</span>
                  <ArrowRight size={13} />
                </Link>
              </>
            ) : (
              <>
                <button
                  onClick={loadDashboard}
                  className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-white/30 px-4 py-2 text-xs text-slate-300 hover:text-white transition-colors"
                  data-testid="dashboard-refresh-button"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  onClick={guardedConnect}
                  disabled={connectLoading}
                  data-testid="dashboard-connect-card-button"
                  aria-label="Add a credit card"
                  title="Add a credit card"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)] disabled:opacity-60"
                >
                  <span className="hidden sm:inline">
                    Add a credit card
                  </span>
                  <Plus size={18} className="sm:hidden" />
                  <Plus size={14} className="hidden sm:inline" />
                </button>
                {!demoMode && (
                  <button
                    onClick={handleSyncCards}
                    disabled={syncingCards}
                    data-testid="dashboard-sync-cards-button"
                    aria-label="Sync cards"
                    title="Re-sync all linked banks from Plaid"
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/10 hover:border-white/30 bg-white/[0.03] hover:bg-white/[0.06] text-slate-200 hover:text-white w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-2.5 text-xs font-semibold transition-colors disabled:opacity-60"
                  >
                    <RefreshCw size={14} className={syncingCards ? "animate-spin" : ""} />
                    <span className="hidden sm:inline">
                      {syncingCards ? "Syncing…" : "Sync cards"}
                    </span>
                  </button>
                )}
                {user && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="dashboard-user-menu"
                        aria-label="Account menu"
                        className="flex items-center justify-center w-9 h-9 sm:w-9 sm:h-9 rounded-full border border-white/10 hover:border-white/30 overflow-hidden bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-[#00E676]/40 transition-colors flex-shrink-0"
                      >
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name || "You"}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span
                            className="text-xs font-bold text-slate-200"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                          >
                            {(user.name || user.email || "U").trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      sideOffset={10}
                      className="min-w-[240px] bg-[#0B1226]/95 backdrop-blur-xl border-white/10 text-slate-200"
                      data-testid="dashboard-user-menu-content"
                    >
                      <DropdownMenuLabel className="px-3 py-2.5">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold">
                          Signed in as
                        </div>
                        <div
                          className="mt-0.5 text-sm text-white font-semibold truncate"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                          data-testid="dashboard-user-menu-name"
                        >
                          {user.name || user.email?.split("@")[0]}
                        </div>
                        {user.email && (
                          <div
                            className="text-[11px] text-slate-300 truncate"
                            title={user.email}
                          >
                            {user.email}
                          </div>
                        )}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        onSelect={() => setSettingsOpen(true)}
                        data-testid="dashboard-user-menu-preferences"
                        className="text-sm cursor-pointer focus:bg-white/[0.06] focus:text-white"
                      >
                        <SettingsIcon size={13} className="mr-2 text-slate-300" />
                        Preferences
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => navigate("/privacy")}
                        data-testid="dashboard-user-menu-privacy"
                        className="text-sm cursor-pointer focus:bg-white/[0.06] focus:text-white"
                      >
                        <FileText size={13} className="mr-2 text-slate-300" />
                        Privacy policy
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        onSelect={async () => {
                          await logout();
                          toast.success("Signed out");
                          navigate("/", { replace: true });
                        }}
                        data-testid="dashboard-user-menu-signout"
                        className="text-sm cursor-pointer focus:bg-red-500/10 focus:text-red-300 text-red-400"
                      >
                        <LogOut size={13} className="mr-2" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-14 pb-28 md:pb-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#00E676] font-bold">
              Overview
            </div>
            <h1
              className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight text-white"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="dashboard-title"
            >
              Your CardWatch
            </h1>
          </div>
          {data?.customer_id && (
            <span className="text-xs text-slate-300" data-testid="dashboard-customer-id">
              Customer · {data.customer_id.slice(0, 10)}…
            </span>
          )}
        </div>

        {error && (
          <div
            className="mb-8 rounded-xl border border-red-500/30 bg-red-500/[0.06] px-5 py-4 text-sm text-red-300"
            data-testid="dashboard-error"
          >
            {error}
          </div>
        )}

        {/* Pro-expired banner — appears at the top of the dashboard */}
        {!readOnly && (
          <ProExpiredBanner
            user={user}
            onDismiss={() => refresh && refresh()}
            onUpgrade={() => setUpgradeOpen(true)}
          />
        )}

        {/* Due-soon reminders banner */}
        {!remindersDismissed && (
          <DueSoonBanner
            reminders={reminders}
            withinDays={reminderWindow}
            onDismiss={() => setRemindersDismissed(true)}
            onSnooze={readOnly ? null : async (cardId) => {
              // Optimistically remove the card from the banner
              setReminders((prev) => prev.filter((r) => r.card_id !== cardId));
              try {
                await axios.post(
                  `${API}/cards/${cardId}/snooze`,
                  {},
                  { withCredentials: true }
                );
                toast.success("Marked as paid · we won't nag you again this cycle");
              } catch (err) {
                // Re-fetch on failure so the UI stays consistent
                try {
                  const remRes = await axios.get(`${API}/reminders/due_soon`, {
                    params: { user_id: activeUserId, within_days: 2 },
                    withCredentials: true,
                  });
                  setReminders(remRes.data?.data || []);
                } catch (_) {}
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Couldn't snooze this card");
              }
            }}
          />
        )}

        {/* Budget alert banner — 80% + 100% thresholds across category + card caps */}
        {!budgetsDismissed && (
          <BudgetAlertBanner
            rows={budgetAlerts}
            onDismiss={() => setBudgetsDismissed(true)}
          />
        )}

        {/* Streak Ribbon — retention win: live under-threshold streak counter */}
        {!readOnly && (
          <StreakRibbon
            streakDays={data?.settings?.streak_days}
            threshold={data?.settings?.spending_alerts_threshold}
            plan={data?.settings?.plan}
          />
        )}

        {/* Credit Limit Banner — always-visible summary of total limit + utilization */}
        {util?.total_credit_limit > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mt-2 sm:mt-0 mb-5 sm:mb-6"
            data-testid="credit-limit-banner"
          >
            {(() => {
              const limit = util.total_credit_limit;
              const used = util.total_credit_used || 0;
              const pct = util.overall_utilization ?? 0;
              const available = Math.max(0, limit - used);
              const zone =
                pct >= 70
                  ? { color: "#EF4444", label: "High risk", from: "#7F1D1D", to: "#EF4444" }
                  : pct >= 30
                  ? { color: "#F5B841", label: "Watch", from: "#78350F", to: "#F5B841" }
                  : { color: "#00E676", label: "Healthy", from: "#065F46", to: "#00E676" };
              return (
                <div
                  className="relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br from-[#0E172C] to-[#0A1122] px-4 sm:px-7 py-4 sm:py-6"
                  data-testid="credit-limit-banner-inner"
                >
                  {/* Header row: label on the left, zone pill top-right (mobile-only visible up here) */}
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold flex items-center gap-1.5 min-w-0">
                      <CreditCard size={11} className="text-[#00E676] flex-shrink-0" />
                      <span className="truncate">Total Credit Limit</span>
                    </div>
                    <div
                      className="sm:hidden text-[9px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0"
                      style={{ color: zone.color, borderColor: `${zone.color}55` }}
                    >
                      {zone.label}
                    </div>
                  </div>

                  {/* Amount row: big £ on left, big % on right, both size-adaptive */}
                  <div className="flex items-baseline justify-between gap-3">
                    <div
                      className="text-[26px] sm:text-4xl text-white font-semibold tracking-tight tabular-nums leading-tight truncate min-w-0"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid="credit-limit-banner-total"
                    >
                      {currencyFormat(limit, currency)}
                    </div>
                    <div
                      className="text-[26px] sm:text-4xl font-semibold tabular-nums leading-tight flex-shrink-0"
                      style={{ fontFamily: "'Outfit', sans-serif", color: zone.color }}
                      data-testid="credit-limit-banner-util"
                    >
                      {pct}%
                    </div>
                  </div>

                  {/* Subline: available + used (left), zone pill (right, desktop-only) */}
                  <div className="mt-1.5 sm:mt-2 flex items-center justify-between gap-3">
                    <div className="text-[11px] sm:text-sm text-slate-300 min-w-0 truncate">
                      <span className="font-semibold text-white tabular-nums">
                        {currencyFormat(available, currency)}
                      </span>
                      <span className="hidden sm:inline"> available · {currencyFormat(used, currency)} in use</span>
                      <span className="sm:hidden"> free</span>
                    </div>
                    <div
                      className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0"
                      style={{ color: zone.color, borderColor: `${zone.color}55` }}
                    >
                      {zone.label}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3 sm:mt-4 h-2 sm:h-2.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full transition-[width] duration-700 ease-out"
                      style={{
                        width: `${Math.min(100, pct)}%`,
                        background: `linear-gradient(90deg, ${zone.from}, ${zone.to})`,
                      }}
                      data-testid="credit-limit-banner-bar"
                    />
                  </div>
                  <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-500">
                    <span>0%</span>
                    <span className="hidden sm:inline">30% healthy · 70% high risk</span>
                    <span>100%</span>
                  </div>
                </div>
              );
            })()}
          </motion.section>
        )}

        {/* Stat cards */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-5"
        >
          <StatCard
            label="Total balance"
            value={loading ? "…" : currencyFormat(data?.total_balance || 0, currency)}
            sub={
              data?.fx?.converted_count > 0
                ? `Includes ${data.fx.converted_count} ${
                    data.fx.converted_count === 1 ? "account" : "accounts"
                  } converted from ${data.fx.converted_currencies.join(" / ")} · ECB rate ${
                    data.fx.date || "today"
                  }`
                : "Aggregated across every linked credit card"
            }
            icon={Wallet}
            accent
            testid="stat-total-balance"
          />
          <StatCard
            label="Linked cards"
            value={loading ? "…" : String(linkedCards)}
            sub={linkedCards ? "Plaid + manual entries" : "Link your first credit card"}
            icon={CreditCard}
            testid="stat-linked-cards"
          />
          <StatCard
            label="Overall utilization"
            value={
              loading
                ? "…"
                : util?.overall_utilization === null || util?.overall_utilization === undefined
                ? "—"
                : `${util.overall_utilization}%`
            }
            sub={
              util?.total_credit_limit
                ? `${currencyFormat(util.total_credit_used, currency)} of ${currencyFormat(
                    util.total_credit_limit,
                    currency
                  )}`
                : "Add a limit to unlock"
            }
            icon={Gauge}
            testid="stat-utilization"
          />
          <StatCard
            label="This month spending"
            value={loading ? "…" : currencyFormat(monthSpend, currency)}
            sub={
              spendingData.length
                ? `Latest bucket · ${spendingData[spendingData.length - 1].label}`
                : "No transactions yet"
            }
            icon={TrendingDown}
            testid="stat-month-spending"
          />
        </motion.div>

        {/* Optimization band — Pro-only. Shows when any card has a credit_limit
             (Pro user gets full state), OR shows the locked teaser to free users. */}
        {(perCard.some((c) => c.credit_limit) || (!isPro && perCard.length > 0)) && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 scroll-mt-24"
            id="optimizer"
            data-testid="optimization-card-wrapper"
          >
            <ProLock
              locked={!isPro && !readOnly}
              title="Credit Optimizer"
              description="Pay-down suggestions that keep every card under the credit-score-friendly 30% threshold."
              bullets={[
                "Unlimited Cards: Link every card in your wallet for a true portfolio view.",
                "Zero Late Fees: Smart due-date alerts delivered to your phone and email.",
                "Auto-Sync: Stop typing. Real-time bank updates from major financial institutions.",
                "Credit Simulator: Predict your score before you even make a payment.",
              ]}
              onUpgrade={() => setUpgradeOpen(true)}
              testid="optimizer-pro-lock"
            >
              <div
                className="rounded-2xl border border-[#00E676]/25 bg-gradient-to-br from-[#00E676]/[0.06] to-transparent p-6 lg:p-7"
                data-testid="optimization-card"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={17} className="text-[#00E676]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
                      Optimization
                    </div>
                    {(util?.optimization?.suggestions?.length || 0) > 0 ? (
                      <>
                        <h3
                          className="mt-2 text-xl font-semibold text-white tracking-tight"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          Bring overall utilization from{" "}
                          <span className="text-[#00E676]">{util?.overall_utilization ?? "—"}%</span> down to{" "}
                          {util?.optimization?.target_utilization ?? 30}%
                        </h3>
                        <p className="mt-1 text-sm text-slate-300">
                          Pay down these cards to get under the credit-score-friendly 30% threshold.
                        </p>
                        <ul className="mt-5 space-y-2.5">
                          {(util?.optimization?.suggestions || []).map((s) => (
                            <li
                              key={s.card_id}
                              className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
                              data-testid={`optimization-suggestion-${s.card_id}`}
                            >
                              <div className="text-sm">
                                <span className="text-white font-semibold">{s.name}</span>
                                <span className="text-slate-300 ml-3 text-xs">
                                  {s.current_utilization}% → {s.target_utilization}%
                                </span>
                              </div>
                              <div
                                className="text-sm font-semibold text-[#00E676]"
                                style={{ fontFamily: "'Outfit', sans-serif" }}
                              >
                                Pay {currencyFormat(s.pay_down, s.currency || currency)}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <h3
                          className="mt-2 text-xl font-semibold text-white tracking-tight"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          You're already under 30% utilization
                        </h3>
                        <p className="mt-1 text-sm text-slate-300">
                          Overall utilization is{" "}
                          <span className="text-[#00E676] font-semibold">
                            {util?.overall_utilization ?? "—"}%
                          </span>
                          {" "}— well inside the credit-score-friendly zone. Keep it up.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </ProLock>
          </motion.section>
        )}

        {/* Simulator entry — always visible so users can find the tool on any screen. */}
        {perCard.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="mt-5 sm:mt-6"
            data-testid="simulator-entry"
          >
            <button
              type="button"
              onClick={() => navigate("/simulator")}
              className="group w-full text-left rounded-2xl border border-[#7C9CFF]/25 bg-gradient-to-br from-[#7C9CFF]/[0.07] to-transparent hover:border-[#7C9CFF]/50 hover:from-[#7C9CFF]/[0.11] transition-colors p-5 sm:p-6 focus:outline-none focus:ring-2 focus:ring-[#7C9CFF]/40"
              data-testid="simulator-entry-button"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#7C9CFF]/15 border border-[#7C9CFF]/40 flex items-center justify-center flex-shrink-0">
                  <SlidersHorizontal size={17} className="text-[#7C9CFF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#7C9CFF] font-bold">
                    What-if tool
                  </div>
                  <div
                    className="mt-1.5 text-base sm:text-lg font-semibold text-white tracking-tight truncate"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Credit Simulator
                  </div>
                  <div className="mt-0.5 text-xs sm:text-sm text-slate-300 truncate">
                    Model a pay-down or spend spike and see the utilization impact instantly
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  className="text-[#7C9CFF] flex-shrink-0 transition-transform group-hover:translate-x-1"
                />
              </div>
            </button>
          </motion.section>
        )}

        {/* Card Vault */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="mt-8"
        >
          <CardVault
            cards={perCard}
            onRemove={handleRemoveVaultCard}
            onEdit={openManualEdit}
            readOnly={readOnly}
            homeCurrency={homeCurrency}
            fxRates={fxRates}
          />
        </motion.div>

        {/* Unified transaction history — manual balance edits + Plaid bank data */}
        <TransactionHistory userId={activeUserId} refreshKey={activityTick} />

        {/* Where your money goes — MTD spend by merchant category, home-currency */}
        <CategoryBreakdown
          userId={activeUserId}
          refreshKey={activityTick}
          homeCurrency={homeCurrency}
          fxRates={fxRates}
        />

        {/* Spending chart */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="mt-8 bg-[#0E172C] border border-[#1A2849] rounded-2xl p-6 lg:p-8"
          data-testid="spending-chart-card"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
                Spending
              </div>
              <h2
                className="mt-2 text-xl font-semibold text-white tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Monthly cashflow, last 6 months
              </h2>
            </div>
            <span className="text-xs text-slate-300 hidden sm:inline" data-testid="spending-chart-currency">
              in {currency}
            </span>
          </div>
          <div className="h-64" data-testid="spending-chart-container">
            {spendingData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-300">
                Link a credit card to see your spending trend.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spendingData} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E676" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00E676" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1A2849" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748B" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis stroke="#64748B" tickLine={false} axisLine={false} fontSize={12} width={80} />
                  <Tooltip
                    cursor={{ stroke: "#00E676", strokeOpacity: 0.2 }}
                    contentStyle={{
                      background: "#0E172C",
                      border: "1px solid #1A2849",
                      borderRadius: 10,
                      color: "#fff",
                      fontSize: 12,
                    }}
                    formatter={(v) => currencyFormat(v, currency)}
                    labelStyle={{ color: "#94A3B8" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="#00E676"
                    strokeWidth={2.5}
                    fill="url(#spendGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.section>

        {/* Cards + Transactions */}
        <div className="mt-8 grid grid-cols-1 gap-6">
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="bg-[#0E172C] border border-[#1A2849] rounded-2xl p-6 lg:p-8"
            data-testid="linked-cards-card"
          >
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2
                    className="text-xl font-semibold text-white tracking-tight"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Linked credit cards
                  </h2>
                  {plan?.card_limit ? (
                    <button
                      type="button"
                      onClick={() => plan?.at_limit ? setUpgradeOpen(true) : setSettingsOpen(true)}
                      data-testid="plan-usage-badge"
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold transition-colors ${
                        plan.at_limit
                          ? "border-[#F5D742]/50 bg-[#F5D742]/[0.08] text-[#F5D742] hover:bg-[#F5D742]/[0.15]"
                          : "border-white/15 bg-white/[0.03] text-slate-300 hover:border-white/30"
                      }`}
                    >
                      {plan.counts?.total ?? 0} of {plan.card_limit} cards · Free
                    </button>
                  ) : (
                    <span
                      data-testid="plan-usage-badge"
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[#00E676] font-bold"
                    >
                      <Sparkles size={10} /> Pro · Unlimited
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-300 mt-1.5">
                  {linkedCards} card{linkedCards === 1 ? "" : "s"} · {seAccounts.length} via Plaid · {manualCards.length} manual
                </div>
              </div>
              <button
                onClick={openManualCreate}
                data-testid="manual-card-entry-button"
                className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] px-4 py-2 text-xs text-[#00E676] font-semibold transition-colors"
              >
                <Plus size={13} /> Manual Card Entry
              </button>
            </div>

            {perCard.length === 0 ? (
              <EmptyState
                onConnect={guardedConnect}
                onManual={openManualCreate}
                loading={connectLoading}
              />
            ) : (
              <ul className="space-y-3" data-testid="linked-cards-list">
                {perCard.map((c) => {
                  const days = daysUntil(c.due_date);
                  const isManual = c.source === "manual";
                  return (
                    <li
                      key={c.id}
                      className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-4 hover:border-[#00E676]/30 transition-colors"
                      data-testid={`card-item-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: isManual ? "rgba(59, 91, 219, 0.15)" : "rgba(0, 230, 118, 0.12)",
                              border: `1px solid ${isManual ? "rgba(59, 91, 219, 0.35)" : "rgba(0, 230, 118, 0.3)"}`,
                            }}
                          >
                            <CreditCard
                              size={16}
                              style={{ color: isManual ? "#7C9CFF" : "#00E676" }}
                            />
                          </div>
                          <div className="min-w-0">
                            <div
                              className="text-sm text-white font-semibold truncate flex items-center gap-2"
                              data-testid={`card-name-${c.id}`}
                            >
                              {c.nickname || c.name}
                              <span
                                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
                                style={{
                                  color: isManual ? "#7C9CFF" : "#00E676",
                                  borderColor: isManual ? "rgba(124,156,255,0.35)" : "rgba(0,230,118,0.35)",
                                }}
                              >
                                {isManual ? "Manual" : "Plaid"}
                              </span>
                            </div>
                            <div className="text-xs text-slate-300 truncate">
                              {c.nickname
                                ? c.name
                                : c.provider_name || (isManual ? "Manual card entry" : "Card")}
                            </div>
                            {c.due_date && (
                              <div
                                className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-300"
                                data-testid={`card-due-${c.id}`}
                              >
                                <CalendarDays size={11} />
                                Due {shortDate(c.due_date)}
                                {days !== null && (
                                  <span
                                    className={`ml-1 ${
                                      days < 0
                                        ? "text-red-400"
                                        : days <= 7
                                        ? "text-yellow-300"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    ·{" "}
                                    {days < 0
                                      ? `${Math.abs(days)}d late`
                                      : days === 0
                                      ? "today"
                                      : `in ${days}d`}
                                  </span>
                                )}
                              </div>
                            )}
                            {c.previous_due_date && wasRecentlyRolled(c.last_rolled_at) && (
                              <div
                                className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-300 italic"
                                data-testid={`card-rolled-${c.id}`}
                                title={`Auto-rolled from ${shortDate(c.previous_due_date)} to keep this card active`}
                              >
                                <RefreshCw size={9} className="text-[#00E676]/70" />
                                Rolled forward from {shortDate(c.previous_due_date)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div
                            className="text-sm text-white font-semibold"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                          >
                            {currencyFormat(c.balance, c.currency || currency)}
                          </div>
                          {c.credit_limit && (
                            <div className="text-[11px] text-slate-300">
                              of {currencyFormat(c.credit_limit, c.currency || currency)}
                            </div>
                          )}
                          {isManual ? (
                            <div className="mt-1.5 flex items-center justify-end gap-3">
                              <button
                                onClick={() => openManualEdit(c)}
                                className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-[#00E676] transition-colors"
                                data-testid={`card-edit-${c.id}`}
                              >
                                <Pencil size={10} /> Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCard(c.id, c.nickname || c.name)}
                                className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-red-400 transition-colors"
                                data-testid={`card-delete-${c.id}`}
                              >
                                <Trash2 size={10} /> Remove
                              </button>
                            </div>
                          ) : (
                            <div className="mt-1.5 flex items-center justify-end gap-3">
                              <button
                                onClick={() => setEditingSaltEdge(c)}
                                className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-[#00E676] transition-colors"
                                data-testid={`card-edit-${c.id}`}
                              >
                                <Pencil size={10} />{" "}
                                {c.credit_limit ? "Edit" : "Set limit & due date"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {c.utilization !== null && c.utilization !== undefined && (
                        <div className="mt-3.5">
                          <div className="flex items-center justify-between text-[11px] mb-1.5">
                            <span className="text-slate-300 uppercase tracking-widest">Utilization</span>
                            <span
                              className="font-semibold"
                              style={{ color: utilizationColor(c.utilization) }}
                            >
                              {c.utilization}%
                            </span>
                          </div>
                          <UtilizationBar util={c.utilization} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.section>

        </div>

        <div className="mt-10 text-xs text-slate-300 flex items-center gap-2">
          <CardWatchMark size={18} /> Cards linked via Plaid · read-only bank access via open banking
        </div>
      </main>

      <ManualCardDialog
        open={manualOpen}
        onOpenChange={(v) => {
          setManualOpen(v);
          if (!v) setEditingCard(null);
        }}
        onCreated={loadDashboard}
        onSaved={loadDashboard}
        defaultCurrency={homeCurrency}
        mode={editingCard ? "edit" : "create"}
        initial={editingCard}
      />

      <LinkCardChooserDialog
        open={linkChooserOpen}
        onOpenChange={setLinkChooserOpen}
        onChooseManual={() => {
          setEditingCard(null);
          setManualOpen(true);
        }}
        onLinked={() => loadDashboard()}
      />

      <SaltEdgeOverrideDialog
        open={!!editingSaltEdge}
        onOpenChange={(v) => { if (!v) setEditingSaltEdge(null); }}
        card={editingSaltEdge}
        onSaved={loadDashboard}
      />

      <GoCardlessConnectDialog open={gcOpen} onOpenChange={setGcOpen} />

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        plan={plan}
        onUpgraded={loadDashboard}
      />

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={data?.settings}
        plan={plan}
        onSaved={loadDashboard}
        onManagePlan={() => {
          setSettingsOpen(false);
          setUpgradeOpen(true);
        }}
      />

      {!readOnly && <BottomNav onOpenSettings={() => setSettingsOpen(true)} />}
    </div>
  );
};

export default Dashboard;
